import { Textarea, TextInput } from '@d-edge/overnight-hotelier-react'
import { useState } from 'react'
import { usePosScriptMutations } from '@/api/queries'
import type { PosScript, PosScriptTestRequest, PosScriptTestResponse } from '@/api/types'
import { Button } from '@/components/ui/Button'
import { Field } from '@/components/ui/Field'
import { Icons } from '@/components/ui/Icons'
import { Modal } from '@/components/ui/Modal'
import { policyKindLabel } from '@/features/settings/policyTarget'
import { PosPolicyDialog } from '@/features/settings/PosPolicyDialog'
import { useToast } from '@/components/ui/Toast'
import styles from './posScripts.module.css'

interface PosScriptEditorProps {
  script: PosScript | null
  /** Content to load instead of the current version (e.g. the previous one). */
  initialContent?: string
  onClose: () => void
}

type Step = 'request' | 'response' | 'save'

const STEP_LABEL: Record<Step, string> = { request: 'Build RRC request', response: 'Validate on response', save: 'Full validation' }
const NATIVE_INPUT = 'htl-text-input htl-text-input--full-width'

/**
 * Edits one PoS script and runs it through the scraper data provider: first the request builder,
 * then the response handler against a pasted response, and finally the full validation that saves.
 */
export function PosScriptEditor({ script, initialContent, onClose }: PosScriptEditorProps) {
  const { testRrcRequest, testOnResponse, save } = usePosScriptMutations()
  const toast = useToast()
  const [posId, setPosId] = useState(script ? String(script.posId) : '')
  const [posName, setPosName] = useState(script?.posName ?? '')
  const [competitorId, setCompetitorId] = useState('')
  const [currency, setCurrency] = useState('EUR')
  const [countryCode, setCountryCode] = useState('FR')
  const [content, setContent] = useState(initialContent ?? script?.currentVersion ?? '')
  const [responseUrl, setResponseUrl] = useState('')
  const [statusCode, setStatusCode] = useState('200')
  const [responseContent, setResponseContent] = useState('')
  const [result, setResult] = useState<{ step: Step; response: PosScriptTestResponse } | null>(null)
  const [showPolicy, setShowPolicy] = useState(false)

  const busy = testRrcRequest.isPending || testOnResponse.isPending || save.isPending
  const ready = Number(posId) > 0 && posName.trim().length > 0 && content.trim().length > 0
  const hasResponse = responseContent.trim().length > 0
  const policyReady = Number(posId) > 0 && posName.trim().length > 0
  // A disabled button cannot explain itself, so the missing input is named in a visible line under the steps.
  const hint = !ready
    ? 'Fill in the POS id, POS name and script to enable the tests.'
    : !hasResponse
      ? 'Step 2 runs the response handler on the response returned by the site. Paste that response in "Response content" to enable it.'
      : null

  const request = (withResponse: boolean): PosScriptTestRequest => ({
    currency: currency || null,
    countryCode: countryCode || null,
    posId: Number(posId),
    posName: posName.trim(),
    competitorId: Number(competitorId) || 0,
    posScript: content,
    rrcResponse: withResponse
      ? { url: responseUrl || null, httpStatusCode: Number(statusCode) || 200, responseBodyUri: null, responseHeaders: null, responseCookies: null, session: null }
      : null,
    responseContent: withResponse ? responseContent : null,
  })

  const run = async (step: Step) => {
    try {
      const response =
        step === 'request' ? await testRrcRequest.mutateAsync(request(false)) : step === 'response' ? await testOnResponse.mutateAsync(request(true)) : await save.mutateAsync(request(true))
      setResult({ step, response })
      if (step === 'save') {
        toast.notify(`Script for ${posName} saved`, 'success')
        onClose()
      }
    } catch (err) {
      toast.error(err, step === 'save' ? 'Save refused' : 'Validation failed')
    }
  }

  return (
    <Modal
      title={script ? `${script.posName} · script` : 'New PoS script'}
      onClose={onClose}
      size="full"
      headerExtra={
        <Button size="sm" icon={<Icons.Settings size={12} />} disabled={!policyReady} onClick={() => setShowPolicy(true)} title="Opens the policy of this POS: its channel policy, or its booking engine policy for POS 17">
          Open {policyKindLabel(Number(posId))} policy
        </Button>
      }
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>Cancel</Button>
          <Button variant="primary" disabled={!ready} loading={save.isPending} onClick={() => void run('save')} title="Runs the full validation, then stores the script">
            Validate and save
          </Button>
        </>
      }
    >
      <div className={styles.layout}>
        <div className={styles.column}>
          <div className={styles.meta}>
            <Field label="POS id" htmlFor="pos-id" required>
              <input id="pos-id" type="number" min={1} value={posId} disabled={!!script} onChange={(e) => setPosId(e.target.value)} className={NATIVE_INPUT} />
            </Field>
            <Field label="POS name" required>
              <TextInput fullWidth value={posName} disabled={!!script} onChange={(e) => setPosName(e.target.value)} />
            </Field>
            <Field label="Competitor id" htmlFor="pos-competitor" hint="Used by the test requests">
              <input id="pos-competitor" type="number" min={0} value={competitorId} onChange={(e) => setCompetitorId(e.target.value)} className={NATIVE_INPUT} />
            </Field>
            <Field label="Currency">
              <TextInput fullWidth value={currency} maxLength={3} onChange={(e) => setCurrency(e.target.value.toUpperCase())} />
            </Field>
            <Field label="Country">
              <TextInput fullWidth value={countryCode} maxLength={2} onChange={(e) => setCountryCode(e.target.value.toUpperCase())} />
            </Field>
          </div>
          <Textarea className={`${styles.script} u-mono`} fullWidth resize="vertical" value={content} onChange={(e) => setContent(e.target.value)} spellCheck={false} aria-label="Script content" placeholder="Paste the PoS script here" />
        </div>

        <div className={styles.column}>
          <div className={styles.steps}>
            <Button size="sm" disabled={!ready} loading={testRrcRequest.isPending} onClick={() => void run('request')} aria-describedby={ready ? undefined : 'pos-steps-hint'} title={ready ? undefined : (hint ?? undefined)}>
              1 · Test request builder
            </Button>
            <Button
              size="sm"
              disabled={!ready || !hasResponse}
              loading={testOnResponse.isPending}
              onClick={() => void run('response')}
              aria-describedby={ready && hasResponse ? undefined : 'pos-steps-hint'}
              title={ready && hasResponse ? undefined : (hint ?? undefined)}
            >
              2 · Test response handler
            </Button>
          </div>
          {hint && (
            <p id="pos-steps-hint" className={styles.stepHint}>
              <Icons.Info size={14} />
              <span>{hint}</span>
            </p>
          )}
          <div className="u-row">
            <Field label="Response URL" className="u-grow">
              <TextInput fullWidth type="url" value={responseUrl} onChange={(e) => setResponseUrl(e.target.value)} placeholder="https://" />
            </Field>
            <Field label="HTTP status" htmlFor="pos-status">
              <input id="pos-status" type="number" value={statusCode} onChange={(e) => setStatusCode(e.target.value)} className="htl-text-input" style={{ width: 90 }} />
            </Field>
          </div>
          <Field label="Response content" hint="Body returned by the site, used by step 2 and by the full validation">
            <Textarea className={`${styles.response} u-mono`} fullWidth resize="vertical" value={responseContent} onChange={(e) => setResponseContent(e.target.value)} spellCheck={false} />
          </Field>
          {result && (
            <div className={`${styles.result} ${result.response.isSuccessfulExecution ? styles.resultOk : styles.resultFail}`}>
              <strong>
                {STEP_LABEL[result.step]}: {result.response.isSuccessfulExecution ? 'success' : 'failed'}
              </strong>
              {result.response.error && <pre>{result.response.error}</pre>}
              {result.response.rrcRequest && <pre>{result.response.rrcRequest}</pre>}
              {result.response.responseValidationResult && <pre>{result.response.responseValidationResult}</pre>}
            </div>
          )}
        </div>
      </div>
      {showPolicy && <PosPolicyDialog channelId={Number(posId)} name={posName.trim()} onClose={() => setShowPolicy(false)} />}
    </Modal>
  )
}
